import { escapeRegExp, keyBy } from 'lodash';
import React from 'react';
import { Link } from '@shared/components/link';
import { Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { MBText, MBTextProps } from '../MBText';
import { MBFormattedTextProps } from './props';

interface MBFormattedTextSegmentProps extends MBTextProps {
  /*
        Color of all text segments with a valid token
        (aka the segments we do special formatting of)
    */
  highlightColor?: string;

  token?: API.ChatMetaTokenFragment;
}

/*
    Formats a token with a given substring
*/
class MBFormattedTextSegment extends React.PureComponent<MBFormattedTextSegmentProps> {
  render() {
    const { children, token, highlightColor, ...otherProps } = this.props;
    const defaultProps = {
      ...otherProps,
      children,
      color: highlightColor,
      webClassName: 'bubble',
    };

    if (!token) {
      return <MBText {...otherProps}>{children}</MBText>;
    }

    if (token.type === 'url') {
      return <LinkToken token={token} {...defaultProps} />;
    }

    if (token.type === 'mention') {
      return <MBText bold {...defaultProps} />;
    }

    if (token.type === 'email') {
      return <LinkToken token={token} {...defaultProps} />;
    }

    if (token.type === 'hashtag') {
      return <MBText bold {...defaultProps} />;
    }

    if (token.type === null) {
      return <MBText {...defaultProps} />;
    }

    return <MBText {...otherProps}>{children}</MBText>;
  }
}

/*
    Formats text like "#hello @all king@coastapp.com is my http://email.com"
    Note: this uses API specific data (API.ChatMetaTokenFragment), but I figured
    it should be under standard components since it might be useful later
    in other contexts too.
*/
export function MBFormattedText({
  tokens,
  children: text,
  highlightColor = Colors.v2.purple,
  ...otherProps
}: MBFormattedTextProps) {
  // If there are no tokens, the regex below would just shread the string
  // in pieces (regex /(|)/ turns "asd" => ["a", "s", "d"] ), which would
  // make ex emojis not work
  if (!tokens.length) {
    return (
      <MBText webClassName="bubble" {...otherProps}>
        {text}
      </MBText>
    );
  }

  const tokensFromValue: Dict<API.ChatMetaTokenFragment> = keyBy(tokens, 'value');
  // Split text into segments based on the token values
  // "hello @all done" => ["hello ", "@all", " done"]
  // For reference: https://stackoverflow.com/q/54330321/9117995
  const values = tokens
    .map(token => token.value)
    // Need to escape strings, otherwise eg links with "?" fail
    // https://stackoverflow.com/a/29700268
    .map(escapeRegExp);
  // Create regex that finds the token values that we want to format
  const regex = new RegExp(`(${values.join('|')})`);
  // Remove all empty strings (eg when splitting "@all" => ["", "@all", ""])
  const substrings = text.split(regex).filter(split => !!split);

  return (
    <MBText>
      {substrings.map((substring: string, index: number) => (
        <MBFormattedTextSegment
          {...otherProps}
          key={index}
          token={tokensFromValue[substring]}
          highlightColor={highlightColor}
        >
          {substring}
        </MBFormattedTextSegment>
      ))}
    </MBText>
  );
}

interface LinkTokenProps extends MBTextProps {
  token: API.ChatMetaTokenFragment;
}

function LinkToken({ token, children, ...props }: LinkTokenProps) {
  const style = {
    color: 'inherit',
    font: 'inherit',
    textDecoration: 'inherit',
  };

  return (
    <MBText bold {...props}>
      <Link href={token.href} style={style} underline={false}>
        {children}
      </Link>
    </MBText>
  );
}
